import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import {
  FaBroadcastTower,
  FaWindowMinimize,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { Link } from "react-router-dom";

import RadioLivePanel from "./RadioLivePanel";
import "./RadioMapWidget.css";

const MIN_WIDTH = 200;
const MAX_WIDTH = 440;
const DEFAULT_WIDTH = 300;
const FAB_SIZE = 48;
const FAB_DEFAULT_TOP = 132;
const DRAG_THRESHOLD = 6;

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

class RadioPanelGuard extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  componentDidCatch() {
    this.setState({ failed: true });
  }

  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

class RadioMapWidget extends Component {
  constructor(props) {
    super(props);
    const saved = this.loadPref();
    const fallback = this.defaultPosition();
    const hasCustomPos =
      typeof saved.left === "number" && typeof saved.top === "number";
    this.state = {
      minimized: true,
      badgeSince: saved.badgeSince || new Date().toISOString(),
      left: hasCustomPos ? saved.left : fallback.left,
      top: hasCustomPos ? saved.top : fallback.top,
      width:
        typeof saved.width === "number"
          ? clamp(saved.width, MIN_WIDTH, MAX_WIDTH)
          : DEFAULT_WIDTH,
      unplayedCount: 0,
      dragging: false,
    };
    this.moving = false;
    this.didDrag = false;
    this.resizing = false;
    this.moveStartX = 0;
    this.moveStartY = 0;
    this.moveStartLeft = 0;
    this.moveStartTop = 0;
    this.resizeStartX = 0;
    this.resizeStartWidth = 0;
    this.resizeStartLeft = 0;
    this.hasCustomPos = hasCustomPos;
  }

  componentDidMount() {
    this.placeOnMap();
    window.addEventListener("resize", this.placeOnMap);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.placeOnMap);
    this.endMove();
    this.endResize();
  }

  getStorageKey = () => {
    return "radio-map-widget-v4-" + (this.props.farmId || "");
  };

  getMapRect = () => {
    const container = document.querySelector(".map-container");
    if (!container) {
      return { width: 800, height: 600, left: 0, top: 0 };
    }
    return container.getBoundingClientRect();
  };

  defaultPosition = (rect) => {
    const box = rect || this.getMapRect();
    return {
      left: Math.max(8, Math.round(box.width - FAB_SIZE - 18)),
      top: FAB_DEFAULT_TOP,
    };
  };

  isOnMap = (left, top, rect) => {
    if (typeof left !== "number" || typeof top !== "number") return false;
    if (!Number.isFinite(left) || !Number.isFinite(top)) return false;
    return (
      left >= 0 &&
      top >= 0 &&
      left <= rect.width - FAB_SIZE &&
      top <= rect.height - FAB_SIZE
    );
  };

  placeOnMap = () => {
    const rect = this.getMapRect();
    const next =
      this.hasCustomPos && this.isOnMap(this.state.left, this.state.top, rect)
        ? this.clampFabPosition(this.state.left, this.state.top)
        : this.defaultPosition(rect);
    if (!this.hasCustomPos || !this.isOnMap(this.state.left, this.state.top, rect)) {
      this.hasCustomPos = false;
    }
    if (next.left === this.state.left && next.top === this.state.top) return;
    this.setState(next);
  };

  loadPref = () => {
    try {
      const raw = localStorage.getItem(this.getStorageKey());
      if (!raw) return {};
      const saved = JSON.parse(raw) || {};
      if (typeof saved.left !== "number" && saved.dock) {
        saved.left = saved.dock === "left" ? 10 : null;
      }
      return saved;
    } catch (e) {
      return {};
    }
  };

  savePref = () => {
    const { minimized, left, top, width, badgeSince } = this.state;
    const data = { minimized, left, top, width };
    if (minimized && badgeSince) {
      data.badgeSince = badgeSince;
    }
    try {
      localStorage.setItem(this.getStorageKey(), JSON.stringify(data));
    } catch (e) {}
  };

  onUnplayedCountChange = (count) => {
    if (count !== this.state.unplayedCount) {
      this.setState({ unplayedCount: count });
    }
  };

  clampFabPosition = (left, top) => {
    const rect = this.getMapRect();
    return {
      left: clamp(left, 8, Math.max(8, rect.width - FAB_SIZE - 8)),
      top: clamp(top, 8, Math.max(8, rect.height - FAB_SIZE - 8)),
    };
  };

  endMove = () => {
    this.moving = false;
    this.didDrag = false;
    document.removeEventListener("mousemove", this.onMoveDrag);
    document.removeEventListener("mouseup", this.onMoveEnd);
    document.removeEventListener("mousemove", this.onFabDrag);
    document.removeEventListener("mouseup", this.onFabMouseUp);
  };

  endResize = () => {
    this.resizing = false;
    document.removeEventListener("mousemove", this.onResizeDrag);
    document.removeEventListener("mouseup", this.onResizeEnd);
  };

  onMoveStart = (event) => {
    if (event.button !== 0) return;
    event.preventDefault();
    this.moving = true;
    this.moveStartX = event.clientX;
    this.moveStartY = event.clientY;
    this.moveStartLeft = this.state.left || 0;
    this.moveStartTop = this.state.top;
    document.addEventListener("mousemove", this.onMoveDrag);
    document.addEventListener("mouseup", this.onMoveEnd);
  };

  onMoveDrag = (event) => {
    if (!this.moving) return;
    const next = this.clampFabPosition(
      this.moveStartLeft + (event.clientX - this.moveStartX),
      this.moveStartTop + (event.clientY - this.moveStartY)
    );
    this.setState(next);
  };

  onFabMouseDown = (event) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    this.moving = true;
    this.didDrag = false;
    this.moveStartX = event.clientX;
    this.moveStartY = event.clientY;
    this.moveStartLeft = this.state.left || 0;
    this.moveStartTop = this.state.top;
    document.addEventListener("mousemove", this.onFabDrag);
    document.addEventListener("mouseup", this.onFabMouseUp);
  };

  onFabDrag = (event) => {
    if (!this.moving) return;
    const dx = event.clientX - this.moveStartX;
    const dy = event.clientY - this.moveStartY;
    if (!this.didDrag && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) {
      return;
    }
    this.didDrag = true;
    const next = this.clampFabPosition(
      this.moveStartLeft + dx,
      this.moveStartTop + dy
    );
    this.setState({ ...next, dragging: true });
  };

  onFabMouseUp = () => {
    const didDrag = this.didDrag;
    this.endMove();
    this.setState({ dragging: false }, () => {
      if (didDrag) {
        this.hasCustomPos = true;
        this.savePref();
        return;
      }
      this.toggle();
    });
  };

  onMoveEnd = () => {
    this.endMove();
    this.hasCustomPos = true;
    this.savePref();
  };

  onResizeStart = (event) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    this.resizing = true;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.state.width;
    this.resizeStartLeft = this.state.left || 0;
    document.addEventListener("mousemove", this.onResizeDrag);
    document.addEventListener("mouseup", this.onResizeEnd);
  };

  onResizeDrag = (event) => {
    if (!this.resizing) return;
    const rect = this.getMapRect();
    const delta = event.clientX - this.resizeStartX;
    let width = clamp(
      this.resizeStartWidth - delta,
      MIN_WIDTH,
      MAX_WIDTH
    );
    let left = this.resizeStartLeft + (this.resizeStartWidth - width);
    left = clamp(left, 8, rect.width - width - 8);
    this.setState({ width, left });
  };

  onResizeEnd = () => {
    this.endResize();
    this.savePref();
  };

  minimize = () => {
    this.setState(
      {
        minimized: true,
        badgeSince: new Date().toISOString(),
        unplayedCount: 0,
      },
      this.savePref
    );
  };

  restore = () => {
    this.setState({ minimized: false, unplayedCount: 0 }, this.savePref);
  };

  toggle = () => {
    if (this.state.minimized) {
      this.restore();
      return;
    }
    this.minimize();
  };

  renderPanel = () => {
    const { minimized, badgeSince } = this.state;
    return (
      <RadioLivePanel
        farmId={this.props.farmId}
        compact
        hideHeader
        onUnplayedCountChange={this.onUnplayedCountChange}
        unplayedBadgeSince={minimized ? badgeSince : null}
      />
    );
  };

  render() {
    const { farmId, t } = this.props;
    const { minimized, left, top, width, unplayedCount, dragging } = this.state;

    const badge =
      unplayedCount > 99 ? "99+" : unplayedCount > 0 ? String(unplayedCount) : "";
    const panelLeft = clamp(left + 24 - width / 2, 8, Math.max(8, this.getMapRect().width - width - 8));
    const panelTop = top + 56;

    return (
      <React.Fragment>
        <button
          type="button"
          className={
            "radio-map-widget-fab" +
            (minimized ? "" : " radio-map-widget-fab--open") +
            (dragging ? " radio-map-widget-fab--dragging" : "")
          }
          style={{ left: left + "px", top: top + "px" }}
          onMouseDown={this.onFabMouseDown}
          title={
            t("Arraste para posicionar") +
            " · " +
            (minimized
              ? unplayedCount > 0
                ? t("Abrir rádio ao vivo") +
                  " (" +
                  unplayedCount +
                  " " +
                  t("novos") +
                  ")"
                : t("Abrir rádio ao vivo")
              : t("Minimizar"))
          }
        >
          <FaBroadcastTower />
          {minimized && badge ? (
            <span className="radio-map-widget-fab-badge">{badge}</span>
          ) : null}
        </button>

        <div
          className="radio-map-widget"
          style={{
            left: panelLeft + "px",
            top: panelTop + "px",
            width: width + "px",
            visibility: minimized ? "hidden" : "visible",
            pointerEvents: minimized ? "none" : "auto",
          }}
          aria-hidden={minimized}
        >
          <div
            className="radio-map-widget-resize"
            onMouseDown={this.onResizeStart}
            title={t("Arraste para redimensionar")}
            role="separator"
            aria-orientation="vertical"
          />
          <div className="radio-map-widget-body">
            <div
              className="radio-map-widget-toolbar"
              onMouseDown={this.onMoveStart}
              title={t("Arraste para mover no mapa")}
            >
              <span className="radio-map-widget-drag-label">
                <FaBroadcastTower /> {t("Rádio ao vivo")}
              </span>
              <span className="radio-map-widget-toolbar-actions">
                <Link
                  className="radio-map-widget-icon-btn"
                  to={"/" + farmId + "/radio/"}
                  title={t("Abrir Rádio")}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <FaExternalLinkAlt />
                </Link>
                <button
                  type="button"
                  className="radio-map-widget-icon-btn"
                  onClick={this.minimize}
                  onMouseDown={(e) => e.stopPropagation()}
                  title={t("Minimizar")}
                >
                  <FaWindowMinimize />
                </button>
              </span>
            </div>
            <RadioPanelGuard>{this.renderPanel()}</RadioPanelGuard>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default withTranslation()(RadioMapWidget);
